/* ============================================================
   AM2T — global-announcement-bar.js
   <promo-bar> ticker (sections/global-announcement-bar.liquid).
   - Messages scroll right→left in a seamless loop.
   - Hover / focus slows it down smoothly (doesn't stop).
   - Click outside a link scrolls to the section after the hero.

   Performance: the old version moved the track in a
   requestAnimationFrame loop that ran forever (even with the bar off
   screen). Now the movement is a Web Animation (runs on the
   compositor), JS only eases the playback rate on hover, and the
   animation pauses while the bar is not visible.
   ============================================================ */

(() => {
  'use strict';

  if (customElements.get('promo-bar')) return;

  class PromoBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.promo-bar__track');
      this.group = this.querySelector('.promo-bar__group');
      if (!this.track || !this.group) return;

      this.speed = parseFloat(this.dataset.speed) || 20; // px per second
      const hover = parseFloat(this.dataset.hoverSpeed);
      this.hoverFactor = Number.isNaN(hover) ? 0.3 : Math.min(Math.max(hover, 0), 100) / 100;
      this.scrollNext = this.dataset.scrollNext === 'true';
      this.isHome = this.dataset.isHome === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.rate = 1;
      this.targetRate = 1;
      this.lastWidth = 0;
      if (this.baseHTML === undefined) this.baseHTML = this.group.innerHTML;

      this.build();

      window.addEventListener('resize', this.handleResize);
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      this.addEventListener('click', this.handleClick);

      // Re-measure once fonts are ready (text width changes).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.build());

      // Pause while off screen.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          if (!this.animation) return;
          if (entry.isIntersecting) this.animation.play();
          else this.animation.pause();
        });
        this.io.observe(this);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.markClickable, { once: true });
      } else {
        this.markClickable();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      clearTimeout(this.resizeTimer);
      cancelAnimationFrame(this.rateRaf);
      this.io?.disconnect();
      this.animation?.cancel();
    }

    /* ── Hover: ease the playback rate toward the target ── */
    onEnter = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      this.setTarget(this.hoverFactor);
    };

    onLeave = () => this.setTarget(1);

    setTarget(target) {
      this.targetRate = target;
      if (!this.animation || this.rateRaf) return;
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min((now - last) / 1000, 0.1);
        last = now;
        this.rate += (this.targetRate - this.rate) * Math.min(1, dt * 6);
        if (Math.abs(this.targetRate - this.rate) < 0.01) this.rate = this.targetRate;
        this.animation.playbackRate = this.rate;
        this.rateRaf = this.rate === this.targetRate ? null : requestAnimationFrame(step);
      };
      this.rateRaf = requestAnimationFrame(step);
    }

    /* ── Click: scroll to the section after the hero ── */
    markClickable = () => {
      if (this.scrollNext && this.getNextSection()) this.classList.add('is-clickable');
    };

    getNextSection = () => {
      const sections = Array.from(document.querySelectorAll('.shopify-section')).filter((s) =>
        !s.parentElement.closest('.shopify-section') &&
        !/shopify-section-group/.test(s.className) &&
        s.offsetHeight > 0
      );
      let heroIndex = sections.findIndex((s) => /hero/i.test(s.id));
      if (heroIndex === -1 && this.isHome) heroIndex = 0;
      if (heroIndex === -1) return null;
      return sections[heroIndex + 1] || null;
    };

    handleClick = (e) => {
      if (!this.scrollNext || e.target.closest('a')) return;
      const target = this.getNextSection();
      if (!target) return;
      const header = document.querySelector('.site-header');
      const offset = this.offsetHeight + (header ? header.offsetHeight : 0);
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: this.reduceMotion ? 'auto' : 'smooth' });
    };

    /* ── Loop construction ── */
    handleResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.clientWidth !== this.lastWidth) this.build();
      }, 200);
    };

    prepareClone(el) {
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('data-clone', '');
      el.removeAttribute('data-shopify-editor-block');
      el.querySelectorAll('[data-shopify-editor-block]').forEach((n) => n.removeAttribute('data-shopify-editor-block'));
      el.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
      return el;
    }

    build = () => {
      if (!this.track || !this.group) return;

      const progress = this.animation ? this.animation.effect.getComputedTiming().progress || 0 : 0;
      this.animation?.cancel();
      this.track.querySelectorAll('.promo-bar__group[data-clone]').forEach((n) => n.remove());
      this.group.innerHTML = this.baseHTML;
      this.lastWidth = this.clientWidth;

      if (this.reduceMotion) return;

      const viewport = this.lastWidth || window.innerWidth;
      const originals = Array.from(this.group.children);
      if (!originals.length) return;

      // Repeat messages until one group is at least as wide as the screen.
      let guard = 0;
      while (this.group.getBoundingClientRect().width < viewport && guard < 40) {
        originals.forEach((item) => this.group.appendChild(this.prepareClone(item.cloneNode(true))));
        guard++;
      }

      // One full group width per cycle + an identical copy for a seamless loop.
      const loopWidth = this.group.getBoundingClientRect().width;
      this.track.appendChild(this.prepareClone(this.group.cloneNode(true)));
      if (!loopWidth || !this.track.animate) return;

      this.animation = this.track.animate(
        [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${-loopWidth}px, 0, 0)` }],
        { duration: (loopWidth / this.speed) * 1000, iterations: Infinity, easing: 'linear' }
      );
      this.animation.currentTime = progress * (loopWidth / this.speed) * 1000;
      this.animation.playbackRate = this.rate;
    };
  }

  customElements.define('promo-bar', PromoBar);
})();
